import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { DropDownComponentProps } from './DropDownComponent.types';
import { StyledDropDownComponent } from './DropDownComponent.styles';
import { DropDownModal, InputTextField } from '@marriott/mi-ui-library';

export const DropDownComponent: FC<DropDownComponentProps> = props => {
  const {
    className,
    onClick,
    dropdownName,
    dropdownValue,
    dropdownPlaceholder,
    isError,
    errorMessage,
    inputValue,
    selectedOption,
    panelClassName,
    mandatory,
    disabled,
    isTabletView,
    dropdownLabel,
    iconAriaLabel,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  /**
   * If DropDown option is clicked closes the Dropdown...
   */

  const optionSelected = (e: { key: string; value: string }) => {
    onClick(e);
    setIsOpen(false);
  };

  /**
   * Open's the dropdown on the Dropdown input click...
   */
  const inputFocus = () => {
    setIsOpen(true);
  };

  /**
   * Input Element Reference..
   */
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Dropdown Element Reference..
   */
  const dropDownRef = useRef<HTMLDivElement>(null);

  /**
   * Selected Input DropDown Element Reference..
   */
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  /**
   * function to close dropdown when focus leaves both input and dropdown or when we click outside the dropdown....
   */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDropDownBlur = (event: any) => {
    const relatedTarget = event.relatedTarget;
    if (
      (relatedTarget && !inputRef?.current?.contains(relatedTarget) && !dropDownRef.current?.contains(relatedTarget)) ||
      !relatedTarget
    ) {
      setIsOpen(false);
    }
  };

  /**
   * scrolls up to the selected dropdown input to be viewed on UI
   */
  useEffect(() => {
    if (isOpen && selectedOptionRef.current) {
      selectedOptionRef.current.scrollIntoView({ behavior: 'auto', inline: 'nearest', block: 'center' });
    }
  }, [isOpen]);

  /**
   * Creating the rfpApprovalFlag if rfpApprovalRequest-dropdown class is present in the className list
   */
  const rfpApprovalFlag = className?.split(' ')?.some(e => e === 'rfpApprovalRequest-dropdown');

  return (
    <StyledDropDownComponent
      data-testid="trc-DropDownComponent"
      data-component-name="m-trc-DropDownComponent"
      className={clsx(rfpApprovalFlag ? 'col-xl-6 mb-0' : 'col-12 col-xl-6 col-md-8 mb-4', 'mb-md-0 mb-xl-0 mt-xl-4')}
      ref={inputRef}
    >
      <InputTextField
        className={clsx('m-input-field t-font-m', className)}
        label={`${dropdownName}`}
        togglePlaceholder={true}
        inputTextFieldClassName="dropdown-input-field"
        showErrorMessage={isError}
        setErrorHtml={isError}
        messageToShow={errorMessage}
        isClickEnabled={true}
        mandatory={mandatory}
        assignInputValue={inputValue ? true : false}
        inputValue={inputValue}
        messageClass={'error-text t-font-xs pt-1'}
        placeHolderText={dropdownPlaceholder}
        disabled={disabled}
        showIcon={true}
        iconClass={clsx('custom_click_track', isOpen ? 'icon-arrow-up' : 'icon-arrow-down')}
        iconForLabel={isOpen ? 'icon-arrow-up' : 'icon-arrow-down'}
        iconOnClick={inputFocus}
        handleKeyDown={inputFocus}
        getInputProps={() => ({
          readOnly: true,
        })}
        onBlur={handleDropDownBlur}
        iconAriaLabel={iconAriaLabel}
      />

      {isOpen && (
        <div ref={dropDownRef}>
          <DropDownModal
            callhandleBlur={handleDropDownBlur}
            dropDownChildrenClassName="col-12 p-0"
            isAutoScrollEnabled={true}
            dropDownSpecificFlag={false}
            handleBlur={true}
            scrollDownModalCloseState={false}
            labelledBy="trc-dropdown"
            show={true}
            className={`dropdown-container`}
            childrenClassName={'trc-dropdown-main'}
            dropdownModalOpenState={isOpen}
            mobileTakeOver={isTabletView ? false : true}
            setDropdownModalOpenState={setIsOpen}
            mobileModalHeading={dropdownLabel}
            mobileHeaderEnabled={isTabletView ? false : true}
          >
            <ul className={clsx('p-0', panelClassName, isOpen === true ? '' : 'd-none')}>
              {dropdownValue?.map(option => (
                <li
                  tabIndex={option.subList && option.subList?.length > 0 ? -1 : 0}
                  onClick={() => {
                    if (option.subList && option.subList?.length > 0) {
                      return false;
                    } else {
                      optionSelected(option);
                    }
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                      if (option.subList && option.subList?.length > 0) {
                        return false;
                      } else {
                        optionSelected(option);
                      }
                    }
                  }}
                  aria-label={option.value}
                  key={option.key}
                  className={clsx(
                    't-font-m d-block ',
                    option?.value && !option.subList?.length && 'py-1 hover-class',
                    option.key === selectedOption?.key ? 'icon-selected' : '',
                    option.subList && option.subList?.length > 0 ? 'm-exp-dropdown-sublist' : ''
                  )}
                >
                  {option?.value && !option?.yachts && (
                    <div
                      className={clsx(
                        'd-flex align-items-center justify-content-between mx-4 py-1',
                        !option.subList?.length ? 'hover-class' : 'mt-3'
                      )}
                      ref={selectedOption?.key === option?.key ? selectedOptionRef : null}
                    >
                      <div className="d-block">{option?.value}</div>
                      {selectedOption?.key === option?.key && <span className="icon-check"></span>}
                    </div>
                  )}

                  {option.subList && option.subList?.length > 0 && (
                    <div className={clsx('px-4')}>
                      {option.subList?.map((optionHotelList: { key: string; value: string }) => (
                        <div
                          role="list"
                          className={clsx(
                            optionHotelList.key === selectedOption?.key &&
                              'd-flex align-items-center justify-content-between hover-class'
                          )}
                          ref={optionHotelList.key === selectedOption?.key ? selectedOptionRef : null}
                        >
                          <span
                            tabIndex={0}
                            key={optionHotelList.key}
                            onClick={() => {
                              optionSelected(optionHotelList);
                            }}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                                optionSelected(optionHotelList);
                              }
                            }}
                            aria-label={optionHotelList.value}
                            role="listitem"
                            className={clsx(
                              !option?.yachts && 'px-4',
                              't-font-m d-block py-2 hover-class',
                              optionHotelList.key === selectedOption?.key ? 'icon-selected' : ''
                            )}
                          >
                            {optionHotelList?.value}
                          </span>
                          {optionHotelList.key === selectedOption?.key && <span className="icon-check"></span>}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </DropDownModal>
        </div>
      )}
    </StyledDropDownComponent>
  );
};
